import React, { useState, useEffect, useCallback, useMemo } from 'react';
import { getClienti, deleteCliente, exportClientiCSV, deleteMultipleClienti, exportClientiExcel, deleteAllClienti, getLayoutsByModulo, generatePdf } from '../services/api';
import { Table, Form, Spinner, Alert, Button, Row, Col, Dropdown, Pagination, Card, InputGroup, Badge } from 'react-bootstrap';
import { Link, useNavigate } from 'react-router-dom';
import { useMediaQuery } from 'react-responsive';
import ActionToolbar from '../common/ActionToolbar';
import MobileListLayout from '../common/MobileListLayout';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import { faUsers, faUserCheck, faEuroSign, faUser, faPhone, faEnvelope, faMapMarkerAlt, faIdCard, faPlus, faCog, faFileExport, faFileImport, faPrint } from '@fortawesome/free-solid-svg-icons';
import {
  useReactTable,
  getCoreRowModel,
  getSortedRowModel,
  getFilteredRowModel,
  getFacetedRowModel,
  getFacetedUniqueValues,
  flexRender,
  createColumnHelper,
} from '@tanstack/react-table';
import SortController from '../common/SortController';
import ColumnOrderController from '../common/ColumnOrderController';
import FilterPanel from '../common/FilterPanel';
import SortableHeader from '../common/SortableHeader';
import StatsCards from '../common/StatsCards';
import { usePageTitle } from '../context/PageTitleContext';
import { getOptimalColumnWidth, autoResizeColumn, autoFitColumns } from '../common/columnSizingUtils';

const columnHelper = createColumnHelper();

const numericColumns = ['id', 'punti_fedelta'];
const dateColumns = ['data_nascita'];

const allColumnsMap = {
  id: "ID",
  nome: "Nome",
  cognome: "Cognome",
  denominazione_sociale: "Denominazione Sociale",
  codice_fiscale: "Codice Fiscale",
  partita_iva: "Partita IVA",
  email: "Email",
  telefono: "Telefono",
  cellulare: "Cellulare",
  punti_fedelta: "Punti Fedeltà",
  origine: "Origine",
  indirizzo: "Indirizzo",
  cap: "CAP",
  citta: "Città",
  provincia: "Provincia",
  nazione: "Nazione",
  codice_destinatario: "Codice Destinatario",
  pec: "PEC",
  luogo_nascita: "Luogo di Nascita",
  data_nascita: "Data di Nascita",
};

const initialVisibleColumns = ['id', 'nome', 'cognome', 'denominazione_sociale', 'email', 'telefono', 'punti_fedelta', 'origine'];

const visibilityKey = 'table-visibility-clienti';

function ClientiList() {
  const navigate = useNavigate();
  const isMobile = useMediaQuery({ maxWidth: 991 });
  const [clienti, setClienti] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [selectedClientiIds, setSelectedClientiIds] = useState([]);
  const [exporting, setExporting] = useState(false);
  const [exportingExcel, setExportingExcel] = useState(false);
  const [distinctValues, setDistinctValues] = useState({
    origine: [],
    provincia: [],
    citta: [],
  });
  const [filterPanelKey, setFilterPanelKey] = useState(0);

  const [columnVisibility, setColumnVisibility] = useState(() => {
    const saved = localStorage.getItem(visibilityKey);
    if (saved) return JSON.parse(saved);
    return Object.keys(allColumnsMap).reduce((acc, key) => {
      acc[key] = initialVisibleColumns.includes(key);
      return acc;
    }, {});
  });
  
  const [sorting, setSorting] = useState(() => {
    const saved = sessionStorage.getItem('list-sorting-clienti');
    return saved ? JSON.parse(saved) : [];
  });

  const [columnFilters, setColumnFilters] = useState(() => {
    const saved = sessionStorage.getItem('list-filters-clienti');
    return saved ? JSON.parse(saved) : [];
  });

  const [columnOrder, setColumnOrder] = useState(() => {
    const savedOrder = localStorage.getItem('table-column-order-clienti');
    const defaultOrder = ['select', ...Object.keys(allColumnsMap)];
    if (savedOrder) {
        let parsedOrder = JSON.parse(savedOrder);
        parsedOrder = parsedOrder.filter(c => c !== 'select');
        return ['select', ...parsedOrder];
    }
    return defaultOrder;
  });

  const [columnSizing, setColumnSizing] = useState(() => {
    const saved = localStorage.getItem('table-column-sizing-clienti');
    return saved ? JSON.parse(saved) : {};
  });

  const [currentPage, setCurrentPage] = useState(() => {
    try {
      const savedPage = sessionStorage.getItem('list-currentPage-clienti');
      return savedPage ? JSON.parse(savedPage) : 1;
    } catch (e) {
      console.error("Errore durante il parsing di currentPage da sessionStorage", e);
      return 1; // Fallback to default
    }
  });
  const [itemsPerPage, setItemsPerPage] = useState(() => {
    try {
      const savedItemsPerPage = sessionStorage.getItem('list-itemsPerPage-clienti');
      return savedItemsPerPage ? JSON.parse(savedItemsPerPage) : 100;
    } catch (e) {
      console.error("Errore durante il parsing di itemsPerPage da sessionStorage", e);
      return 100; // Fallback to default
    }
  });
  const [totalClienti, setTotalClienti] = useState(0);
  const [reportType, setReportType] = useState('all'); // all, with_cf, without_cf
  const [reportTitle, setReportTitle] = useState('Tutti i Clienti');

  const fetchClientiData = useCallback(async () => {
    setLoading(true);
    setError(null);
    try {
      const filters = columnFilters.reduce((acc, filter) => {
        const { id, value } = filter;
        if (Array.isArray(value)) {
          if (value.length > 0) {
            acc[id] = { 'in': value };
          }
        } else if (typeof value === 'object' && value !== null) {
          // Gestione filtri range (min/max per numerici e date)
          if (value.min !== undefined || value.max !== undefined) {
            if (value.min !== undefined && value.min !== '') {
              acc[`${id}__gte`] = value.min;
            }
            if (value.max !== undefined && value.max !== '') {
              acc[`${id}__lte`] = value.max;
            }
          }
        } else if (typeof value === 'string' && value.toUpperCase() === 'N/A') {
          acc[id] = null;
        } else if (value !== undefined && value !== '') {
          acc[id] = value;
        }
        return acc;
      }, {});

      const sortParams = sorting.map(sort => `${sort.id}:${sort.desc ? 'desc' : 'asc'}`);
      const stringifiedFilters = JSON.stringify(filters);

      const response = await getClienti((currentPage - 1) * itemsPerPage, itemsPerPage, sortParams, stringifiedFilters);
      setClienti(response.data.clienti);
      setTotalClienti(response.data.total);
      if (response.data.facets) {
        setDistinctValues(response.data.facets);
      }
    } catch (err) {
      setError('Errore nel caricamento dei clienti.');
      console.error(err);
    } finally {
      setLoading(false);
    }
  }, [currentPage, itemsPerPage, sorting, columnFilters]);

  useEffect(() => {
    fetchClientiData();
  }, [fetchClientiData]);



  // Nuovo useEffect per gestire reportType
  useEffect(() => {
    setColumnFilters(prevFilters => {
      const otherFilters = prevFilters.filter(f => f.id !== 'codice_fiscale'); // Rimuovi il filtro precedente se esiste
      if (reportType === 'with_cf') {
        return [...otherFilters, { id: 'codice_fiscale', value: { ne: null } }]; // Non nullo
      }
      if (reportType === 'without_cf') {
        return [...otherFilters, { id: 'codice_fiscale', value: null }]; // Nullo
      }
      return otherFilters;
    });

    if (reportType === 'with_cf') {
      setReportTitle('Clienti con Codice Fiscale');
    } else if (reportType === 'without_cf') {
      setReportTitle('Clienti senza Codice Fiscale');
    } else {
      setReportTitle('Tutti i Clienti');
    }
  }, [reportType]);

  const filterFields = useMemo(() => [
    { id: '_global_search', label: 'Cerca', type: 'text', placeholder: 'Nome, cognome, email...', colSize: 4 },
    { id: 'email', label: 'Email', type: 'text', colSize: 3 },
    { id: 'origine', label: 'Origine', type: 'select', options: distinctValues.origine, colSize: 2 },
    { id: 'provincia', label: 'Provincia', type: 'select', options: distinctValues.provincia, colSize: 2 },
    { id: 'citta', label: 'Città', type: 'select', options: distinctValues.citta, colSize: 2 },
  ], [distinctValues]);

  const handleApplyFilters = useCallback((filterValues) => {
    const newColumnFilters = Object.entries(filterValues)
      .filter(([, v]) => v !== undefined && v !== '' && v !== null)
      .map(([id, value]) => ({ id, value }));
    setColumnFilters(newColumnFilters);
    setCurrentPage(1);
  }, []);

  const getColumnValue = (cliente, columnName) => {
    switch (columnName) {
      case 'data_nascita':
        return cliente.data_nascita ? new Date(cliente.data_nascita).toLocaleDateString() : 'N/A';
      case 'origine':
        return cliente.origine || 'N/A';
      default:
        return cliente[columnName] != null ? String(cliente[columnName]) : 'N/A';
    }
  };

  const columns = useMemo(
    () => [
      columnHelper.display({
        id: 'select',
        header: ({ table }) => <Form.Check type="checkbox" onChange={table.getToggleAllRowsSelectedHandler()} checked={table.getIsAllRowsSelected()} disabled={clienti.length === 0} />,
        cell: ({ row }) => <Form.Check type="checkbox" checked={row.getIsSelected()} onChange={row.getToggleSelectedHandler()} onClick={(e) => e.stopPropagation()} />,
        minSize: 30, maxSize: 30, size: 30, enableResizing: false, enableSorting: false, enableColumnFilter: false,
      }),
      ...Object.entries(allColumnsMap).map(([key, value]) =>
        columnHelper.accessor(key, {
          header: value,
          cell: info => getColumnValue(info.row.original, key),
          enableColumnFilter: true,
          meta: {
            filterVariant: ['origine', 'provincia', 'citta'].includes(key) ? 'select' : 'text',
            filterOptions: distinctValues[key],
          },
        })
      ),
    ],
    [clienti, distinctValues]
  );

  const onColumnVisibilityChange = (updater) => {
    const newState = typeof updater === 'function' ? updater(columnVisibility) : updater;
    setColumnVisibility(newState);
    localStorage.setItem(visibilityKey, JSON.stringify(newState));
  };

  const table = useReactTable({
    data: clienti, columns, state: { columnVisibility, sorting, columnFilters, columnOrder, columnSizing },
    onColumnVisibilityChange, onSortingChange: setSorting, onColumnFiltersChange: setColumnFilters, onColumnOrderChange: setColumnOrder, onColumnSizingChange: setColumnSizing,
    getCoreRowModel: getCoreRowModel(), getSortedRowModel: getSortedRowModel(), getFilteredRowModel: getFilteredRowModel(),
    getFacetedRowModel: getFacetedRowModel(), getFacetedUniqueValues: getFacetedUniqueValues(),
    enableColumnResizing: true, columnResizeMode: 'onChange', enableColumnReordering: true, manualFiltering: true,
  });

  const stickyColumns = ['select'];

  const handleAutoResize = (column) => {
    autoResizeColumn(column, table);
  };

  const handleAutoFitAllColumns = () => {
    autoFitColumns(table);
  };

  const handleResetColumnWidths = () => {
    table.resetColumnSizing();
  };

  const stats = useMemo(() => {
    const totalFedelta = clienti.reduce((sum, item) => sum + ((item.punti_fedelta || 0)), 0);
    const clientiConEmail = clienti.filter(item => item.email && item.email !== '').length;
    return [
      { label: 'Totale Clienti', value: totalClienti, icon: faUsers, color: 'primary' },
      { label: 'Con Email', value: clientiConEmail, icon: faUserCheck, color: 'success' },
      { label: 'Punti Fedeltà Totali', value: totalFedelta.toLocaleString(), icon: faEuroSign, color: 'warning' },
    ];
  }, [clienti, totalClienti]);

  useEffect(() => {
    sessionStorage.setItem('list-sorting-clienti', JSON.stringify(sorting));
  }, [sorting]);

  useEffect(() => {
    sessionStorage.setItem('list-filters-clienti', JSON.stringify(columnFilters));
  }, [columnFilters]);

  useEffect(() => {
    sessionStorage.setItem('list-currentPage-clienti', JSON.stringify(currentPage));
  }, [currentPage]);

  useEffect(() => {
    sessionStorage.setItem('list-itemsPerPage-clienti', JSON.stringify(itemsPerPage));
  }, [itemsPerPage]);

  const { rows } = table.getSelectedRowModel();
  useEffect(() => {
    // Estrae gli ID delle righe selezionate e filtra i valori null/undefined
    const newSelectedIds = rows
      .map((row) => row.original.id)
      .filter((id) => id != null);

    setSelectedClientiIds(newSelectedIds);
  }, [rows]);

  const handleRowClick = useCallback((row) => {
    // Prima di navigare, salva lo stato corrente della tabella
    sessionStorage.setItem('list-currentPage-clienti', JSON.stringify(currentPage));
    sessionStorage.setItem('list-itemsPerPage-clienti', JSON.stringify(itemsPerPage));
    sessionStorage.setItem('list-sorting-clienti', JSON.stringify(sorting));
    sessionStorage.setItem('list-filters-clienti', JSON.stringify(columnFilters));
    
    navigate(`/clienti/${row.original.id}`);
  }, [currentPage, itemsPerPage, sorting, columnFilters, navigate]);

  const createDownloadLink = (response, fileName) => {
    const url = window.URL.createObjectURL(new Blob([response.data]));
    const link = document.createElement('a');
    link.href = url;
    link.setAttribute('download', fileName);
    document.body.appendChild(link);
    link.click();
    link.remove();
    window.URL.revokeObjectURL(url);
  };

  const handleExportSelected = async () => {
    setExporting(true);
    try {
      const response = await exportClientiCSV(selectedClientiIds);
      createDownloadLink(response, 'clienti_selezionati.csv');
      table.toggleAllRowsSelected(false);
    } catch (err) { setError('Errore durante l\'esportazione.'); }
    finally { setExporting(false); }
  };

  const handleExportAllExcel = async () => {
    setExportingExcel(true);
    try {
      const response = await exportClientiExcel();
      createDownloadLink(response, 'clienti.xlsx');
    } catch (err) { setError('Errore durante l\'esportazione.'); }
    finally { setExportingExcel(false); }
  };

  const handlePrintWithLayout = async (layoutId) => {
    if (selectedClientiIds.length === 0) return alert("Seleziona almeno un cliente per la stampa.");
    setLoading(true);
    try {
      const response = await generatePdf(layoutId, selectedClientiIds);
      const url = window.URL.createObjectURL(new Blob([response.data], { type: 'application/pdf' }));
      const link = document.createElement('a');
      link.href = url;
      link.setAttribute('download', `documento_layout_${layoutId}.pdf`);
      document.body.appendChild(link);
      link.click();
      link.remove();
      window.URL.revokeObjectURL(url);
    } catch (error) { setError("Errore durante la generazione del PDF."); }
    finally { setLoading(false); }
  };

  const handleResetFilters = () => {
    table.resetColumnFilters();
    setSorting([]);
    setReportType('all');
    setFilterPanelKey(k => k + 1);
  };

  const deleteApiMap = { clienti: deleteCliente };
  const deleteMultipleApiMap = { clienti: deleteMultipleClienti };

  const renderPaginationItems = () => {
    const totalPages = Math.ceil(totalClienti / itemsPerPage);
    const pageNumbers = []; const maxPagesToShow = 5; let startPage, endPage;
    if (totalPages <= maxPagesToShow) { startPage = 1; endPage = totalPages; }
    else {
      if (currentPage <= Math.ceil(maxPagesToShow / 2)) { startPage = 1; endPage = maxPagesToShow; }
      else if (currentPage + Math.floor(maxPagesToShow / 2) >= totalPages) { startPage = totalPages - maxPagesToShow + 1; endPage = totalPages; }
      else { startPage = currentPage - Math.floor(maxPagesToShow / 2); endPage = currentPage + Math.floor(maxPagesToShow / 2); }
    }
    for (let i = startPage; i <= endPage; i++) {
      pageNumbers.push(<Pagination.Item key={i} active={i === currentPage} onClick={() => setCurrentPage(i)}>{i}</Pagination.Item>);
    }
    return pageNumbers;
  };

  // Usiamo SortableHeader dal componente comune invece di definirlo localmente
  
  const hasManualFilters = table.getState().columnFilters.length > 0;

  // Render function per le card mobile
  const renderClienteCard = useCallback((cliente, isSelected, onToggleSelect) => {
    const fullName = cliente.denominazione_sociale || `${cliente.nome || ''} ${cliente.cognome || ''}`.trim() || 'Cliente senza nome';
    
    return (
      <div className="d-flex align-items-start">
        <Form.Check
          type="checkbox"
          checked={isSelected}
          onChange={(e) => onToggleSelect && onToggleSelect(e)}
          className="me-3"
          style={{ marginTop: '2px' }}
        />
        
        <div className="flex-grow-1" onClick={() => navigate(`/clienti/edit/${cliente.id}`)} style={{ cursor: 'pointer' }}>
          <div className="d-flex align-items-center mb-1">
            <FontAwesomeIcon icon={faUser} className="text-primary me-2" />
            <h6 className="mb-0 fw-bold">{fullName}</h6>
          </div>
          
          <small className="text-muted d-block mb-2">
            ID: {cliente.id}
            {cliente.codice_fiscale && ` • CF: ${cliente.codice_fiscale}`}
          </small>
          
          <div>
            {cliente.email && (
              <Badge bg="info" className="me-1" style={{ fontSize: '0.65rem' }}>
                <FontAwesomeIcon icon={faEnvelope} className="me-1" />
                {cliente.email}
              </Badge>
            )}
            {cliente.telefono && (
              <Badge bg="secondary" className="me-1" style={{ fontSize: '0.65rem' }}>
                <FontAwesomeIcon icon={faPhone} className="me-1" />
                {cliente.telefono}
              </Badge>
            )}
            {cliente.codice_fiscale && (
              <Badge bg="warning" className="me-1" style={{ fontSize: '0.65rem' }}>
                CF
              </Badge>
            )}
            {(cliente.citta || cliente.provincia) && (
              <Badge bg="success" className="me-1" style={{ fontSize: '0.65rem' }}>
                <FontAwesomeIcon icon={faMapMarkerAlt} className="me-1" />
                {cliente.citta}{cliente.provincia ? ` (${cliente.provincia})` : ''}
              </Badge>
            )}
            {cliente.punti_fedelta > 0 && (
              <Badge bg="primary" style={{ fontSize: '0.65rem' }}>
                <FontAwesomeIcon icon={faEuroSign} className="me-1" />
                {cliente.punti_fedelta} punti
              </Badge>
            )}
          </div>
        </div>
      </div>
    );
  }, [navigate]);

  // Quick filters per mobile
  const quickFilters = useMemo(() => [
    {
      label: 'Tutti',
      icon: faUsers,
      isActive: reportType === 'all',
      onClick: () => setReportType('all'),
      gradients: ['#6c757d', '#495057']
    },
    {
      label: 'Con CF',
      icon: faIdCard,
      isActive: reportType === 'with_cf',
      onClick: () => setReportType('with_cf'),
      gradients: ['#28a745', '#20c997']
    },
    {
      label: 'Senza CF',
      icon: faUserCheck,
      isActive: reportType === 'without_cf',
      onClick: () => setReportType('without_cf'),
      gradients: ['#ffc107', '#fd7e14']
    }
  ], [reportType]);

  // Actions dropdown content
  const actionsDropdownContent = useMemo(() => (
    <>
      <div className="px-3 py-2 border-bottom">
        <small className="text-muted fw-bold">IMPORTAZIONE</small>
      </div>
      <Dropdown.Item as={Link} to="/nuovo/cliente/import">
        <FontAwesomeIcon icon={faFileImport} className="me-2" />
        Importa da CSV/Excel
      </Dropdown.Item>
      
      <div className="px-3 py-2 border-bottom border-top">
        <small className="text-muted fw-bold">ESPORTAZIONE</small>
      </div>
      <Dropdown.Item onClick={handleExportSelected} disabled={selectedClientiIds.length === 0 || exporting}>
        <FontAwesomeIcon icon={faFileExport} className="me-2" />
        {exporting ? 'Esportazione...' : 'Esporta Selezionati (CSV)'}
      </Dropdown.Item>
      <Dropdown.Item onClick={handleExportAllExcel} disabled={exportingExcel}>
        <FontAwesomeIcon icon={faFileExport} className="me-2" />
        {exportingExcel ? 'Esportazione...' : 'Esporta Tutto (Excel)'}
      </Dropdown.Item>
      
      <div className="px-3 py-2 border-bottom border-top">
        <small className="text-muted fw-bold">STAMPA</small>
      </div>
      <Dropdown.Item onClick={() => handlePrintWithLayout(1)} disabled={selectedClientiIds.length === 0}>
        <FontAwesomeIcon icon={faPrint} className="me-2" />
        Stampa con Layout
      </Dropdown.Item>
    </>
  ), [exporting, exportingExcel, selectedClientiIds.length, handleExportSelected, handleExportAllExcel, handlePrintWithLayout]);

  usePageTitle('Clienti');

  return (
    <>
      <StatsCards stats={stats} />

    <Card>
      <Card.Header className="sticky-card-header">
        {isMobile ? (
          <MobileListLayout
            entityName="Cliente"
            entityNamePlural="Clienti"
            createPath="/clienti/new"
            createButtonText="Nuovo Cliente"
            quickFilters={quickFilters}
            actionsDropdownContent={actionsDropdownContent}
            renderCard={renderClienteCard}
            items={table.getRowModel().rows.map(row => row.original)}
            selectedIds={selectedClientiIds}
            onToggleSelect={(clienteId) => {
              const row = table.getRowModel().rows.find(r => r.original.id === clienteId);
              if (row) row.toggleSelected();
            }}
            onCardClick={(cliente) => navigate(`/clienti/edit/${cliente.id}`)}
            onDeleteMultiple={() => {
              if (window.confirm(`Vuoi eliminare ${selectedClientiIds.length} cliente/i selezionato/i?`)) {
                deleteMultipleClienti(selectedClientiIds).then(() => {
                  fetchClientiData();
                  setSelectedClientiIds([]);
                  if (table) table.toggleAllRowsSelected(false);
                });
              }
            }}
            table={table}
            onSearchChange={(e) => {
              const val = e.target.value;
              setColumnFilters(prev => {
                const without = prev.filter(f => f.id !== '_global_search');
                return val ? [...without, { id: '_global_search', value: val }] : without;
              });
              setCurrentPage(1);
            }}
            searchValue={columnFilters.find(f => f.id === '_global_search')?.value || ''}
            onResetFilters={handleResetFilters}
            hasFilters={hasManualFilters || reportType !== 'all'}
            activeFiltersCount={table.getState().columnFilters.length + (reportType !== 'all' ? 1 : 0)}
          />
        ) : (
        <Row className="align-items-center">
          <Col md={6} className="d-flex justify-content-start align-items-center">
            <Button as={Link} to="/clienti/new" className="me-2" size="sm" variant="success">Nuovo Cliente</Button>
            <Dropdown className="me-2">
              <Dropdown.Toggle variant="primary" id="dropdown-import" size="sm">Importa</Dropdown.Toggle>
              <Dropdown.Menu>
                <Dropdown.Item as={Link} to="/nuovo/cliente/import">Importa da CSV/Excel</Dropdown.Item>
              </Dropdown.Menu>
            </Dropdown>
            <Dropdown className="me-2">
              <Dropdown.Toggle variant="info" id="dropdown-export" size="sm">Esporta</Dropdown.Toggle>
              <Dropdown.Menu>
                <Dropdown.Item onClick={handleExportSelected} disabled={selectedClientiIds.length === 0 || exporting}>{exporting ? 'Esportazione...' : 'Esporta Selezionati (CSV)'}</Dropdown.Item>
                <Dropdown.Item onClick={handleExportAllExcel} disabled={exportingExcel}>{exportingExcel ? 'Esportazione...' : 'Esporta Tutto (Excel)'}</Dropdown.Item>
              </Dropdown.Menu>
            </Dropdown>
            <Dropdown className="me-2">
              <Dropdown.Toggle variant="secondary" id="dropdown-actions" size="sm">Azioni</Dropdown.Toggle>
              <Dropdown.Menu>
                <Dropdown.Item onClick={() => handlePrintWithLayout(1)} disabled={selectedClientiIds.length === 0}>Stampa con Layout</Dropdown.Item>
              </Dropdown.Menu>
            </Dropdown>
          </Col>
          <Col md={6} className="d-flex justify-content-end align-items-center">
            <Form.Group className="me-2"> {/* Filtro reportType */}
              <Form.Select onChange={(e) => setReportType(e.target.value)} value={reportType} size="sm">
                <option value="all">Tutti i Clienti</option>
                <option value="with_cf">Con Codice Fiscale</option>
                <option value="without_cf">Senza Codice Fiscale</option>
              </Form.Select>
            </Form.Group>
            <Dropdown className="me-2" autoClose="outside">
              <Dropdown.Toggle variant="outline-secondary" id="dropdown-sorting" size="sm">Ordinamento</Dropdown.Toggle>
              <Dropdown.Menu className="long-dropdown-menu p-2">
                <SortController columns={table.getAllLeafColumns()} sorting={sorting} setSorting={setSorting} />
              </Dropdown.Menu>
            </Dropdown>
            <Dropdown className="me-2" autoClose="outside">
              <Dropdown.Toggle variant="outline-secondary" id="dropdown-columns" size="sm">Colonne</Dropdown.Toggle>
              <Dropdown.Menu className="long-dropdown-menu p-0" style={{ minWidth: '500px' }}>
                <div className="d-flex">
                  <div className="p-2" style={{ flex: 1, borderRight: '1px solid #dee2e6' }}><ColumnOrderController table={table} allColumnsMap={allColumnsMap} /></div>
                  <div className="p-2" style={{ flex: 1 }}>
                    <h6 className="fw-bold">Mostra Colonne</h6>
                    <div className="d-flex flex-column">
                      {table.getAllColumns().filter(c => c.id !== 'select').map(c => <Form.Check key={c.id} type="checkbox" label={allColumnsMap[c.id]} checked={c.getIsVisible()} onChange={c.getToggleVisibilityHandler()} className="dropdown-item" />)}
                    </div>
                  </div>
                </div>
              </Dropdown.Menu>
            </Dropdown>
            <Button variant="outline-secondary" size="sm" className="ms-2" onClick={handleResetFilters} disabled={!hasManualFilters && reportType === 'all'}>Azzera Filtri</Button>
            <Button variant="outline-primary" size="sm" className="ms-2" onClick={handleAutoFitAllColumns}>Auto-Fit</Button>
            <Button variant="outline-warning" size="sm" className="ms-2" onClick={handleResetColumnWidths}>Reset Colonne</Button>
          </Col>
        </Row>
        )}
      </Card.Header>
      <Card.Body>
        {!isMobile && (
          <FilterPanel
            key={filterPanelKey}
            fields={filterFields}
            onApply={handleApplyFilters}
          />
        )}
        {loading && <div className="text-center"><Spinner animation="border" /></div>}
        {error && <Alert variant="danger">{error}</Alert>}
        {!loading && !error && !isMobile && (
          <div style={{ maxHeight: 'calc(100vh - 350px)', overflow: 'auto' }}>
            <Table striped bordered hover style={{ width: table.getTotalSize() }} className="aligned-table">
            <thead>
                {table.getHeaderGroups().map(headerGroup => (
                  <React.Fragment key={headerGroup.id}>
                    <tr>
                      {headerGroup.headers.map(header => (
                        <SortableHeader 
                          key={header.id} 
                          header={header} 
                          table={table} 
                          handleAutoResize={handleAutoResize}
                          stickyColumns={stickyColumns}
                        />
                      ))}
                    </tr>
                  </React.Fragment>
                ))}
              </thead>
              <tbody>
                {table.getRowModel().rows.map(row => (
                  <tr key={row.id} onClick={() => handleRowClick(row)} className={row.getIsSelected() ? 'table-primary' : ''} style={{ cursor: 'pointer', height: '40px' }}>
                    {row.getVisibleCells().map(cell => (
                      <td key={cell.id} style={{ verticalAlign: 'middle', width: `${cell.column.getSize()}px`, overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>
                        {flexRender(cell.column.columnDef.cell, cell.getContext())}
                      </td>
                    ))}
                  </tr>
                ))}
              </tbody>
            </Table>
          </div>
        )}
        {!isMobile && (
          <ActionToolbar selectedItemIds={selectedClientiIds} entityType="clienti" onDeleteSuccess={fetchClientiData} deleteApiMap={deleteApiMap} deleteMultipleApiMap={deleteMultipleApiMap} />
        )}
      </Card.Body>
      <Card.Footer className="d-flex justify-content-between align-items-center">
        <Form.Group as={Row} className="align-items-center mb-0">
          <Col xs="auto"><Form.Label className="mb-0">Record per pagina</Form.Label></Col>
          <Col xs="auto">
            <Form.Select size="sm" value={itemsPerPage} onChange={(e) => { setItemsPerPage(Number(e.target.value)); setCurrentPage(1); }}>
              <option value={10}>10</option><option value={25}>25</option><option value={50}>50</option><option value={100}>100</option>
            </Form.Select>
          </Col>
        </Form.Group>
        <Pagination size="sm" className="mb-0">
          <Pagination.First onClick={() => setCurrentPage(1)} disabled={currentPage === 1} />
          <Pagination.Prev onClick={() => setCurrentPage(prev => prev - 1)} disabled={currentPage === 1} />
          {renderPaginationItems()}
          <Pagination.Next onClick={() => setCurrentPage(prev => prev + 1)} disabled={currentPage === Math.ceil(totalClienti / itemsPerPage)} />
          <Pagination.Last onClick={() => setCurrentPage(Math.ceil(totalClienti / itemsPerPage))} disabled={currentPage === Math.ceil(totalClienti / itemsPerPage)} />
        </Pagination>
      </Card.Footer>
    </Card>
  </>
  );
}

export default ClientiList;